import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WalletProvider } from './contexts/WalletContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ConfigProvider } from './contexts/ConfigContext';
import { PracticeProvider } from './contexts/PracticeContext';
import { GameProvider } from './contexts/GameContext';
import Header from './components/common/Header';
import Home from './pages/Home';
import HowToPlay from './pages/HowToPlay';
import GamePage from './pages/GamePage';
import GameWindow from './pages/GameWindow';
import GameWindowPage from './pages/GameWindowPage';
import Profile from './pages/Profile';
import Dashboard from './pages/Dashboard';
import Leaderboard from './pages/Leaderboard';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard/AdminDashboard';
import Login from './components/Login';
import Register from './components/Register';
import ResetPassword from './components/ResetPassword';
import CashGames from './pages/CashGames';
import Tournaments from './pages/Tournaments';
import PracticeGames from './pages/PracticeGames';
import PracticeGame from './pages/PracticeGame';
import PracticeRoom from './pages/PracticeRoom';
import AddFunds from './pages/AddFunds';
import TransactionHistory from './pages/TransactionHistory';
import Settings from './pages/Settings';
import PracticeSession from './components/PracticeSession/PracticeSession';
import EnhancedPracticeLobby from './components/EnhancedPracticeLobby/EnhancedPracticeLobby';
import PracticeSessionPage from './pages/PracticeSessionPage';
import EnhancedRummyGame from './components/EnhancedRummyGame/EnhancedRummyGame';
import PracticeStats from './components/PracticeStats/PracticeStats';
import PracticeAchievements from './components/PracticeAchievements/PracticeAchievements';
import './styles/globals.css';
import './styles/animations.css';
import NewGame from './components/NewGame/NewGame';
import GameLayout from './components/GameLayout/GameLayout';
import SuperAdminDashboard from './components/superadmin/SuperAdminDashboard';
import SuperAdminRoute from './components/SuperAdminRoute';
import TestbedBanner from './components/TestbedBanner';
import RegistrationClosed from './components/RegistrationClosed';
import { isTestbed, isRegistrationOpen } from './config/environment';

// ==================== MULTIPLAYER COMPONENTS ====================
import MultiplayerGameLobby from './components/Multiplayer/MultiplayerGameLobby';
import MultiplayerGameRoom from './components/Multiplayer/MultiplayerGameRoom';
import MultiplayerGameLayout from './components/Multiplayer/MultiplayerGameLayout';
import FreePlayWaitingRoom from './components/Multiplayer/FreePlayWaitingRoom';
import FreePlay from './components/Multiplayer/FreePlay';
import FreePlayGameSelection from './components/Multiplayer/FreePlayGameSelection';
import CashRummy from './components/Multiplayer/CashRummy/CashRummy';
import CashRummyWaitingRoom from './components/Multiplayer/CashRummy/CashRummyWaitingRoom';
import Tournament from './components/Multiplayer/Tournament/Tournament';
import TournamentResults from './components/Multiplayer/Tournament/TournamentResults';
import TournamentMonitor from './components/Multiplayer/Tournament/TournamentMonitor';
import ForceChangePassword from './components/ForceChangePassword';
import CustomRoomLobby from './components/Multiplayer/CustomRoomLobby';

// ==================== GAME SELECTION COMPONENTS ====================
import GameSelection from './components/GameSelection/GameSelection';

import './App.css';

// ==================== ROUTE COMPONENTS ====================
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="error-boundary">
          <h3>Something went wrong</h3>
          <p>{this.state.error?.message || 'Unknown error'}</p>
          <button onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading enhanced Rummy game...</p>
      </div>
    );
  }
  
  return currentUser ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading admin panel...</p>
      </div>
    );
  }
  
  return currentUser && isAdmin() ? children : <Navigate to="/admin/login" />;
};

const PublicRoute = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!currentUser) return children;
  return isAdmin() ? <Navigate to="/admin/dashboard" /> : <Navigate to="/games" />;
};

const AdminPublicRoute = ({ children }) => {
  const { currentUser, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!currentUser) return children;
  return isAdmin() ? <Navigate to="/admin/dashboard" /> : <Navigate to="/games" />;
};

const AppRedirect = () => {
  const { currentUser, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading enhanced Rummy...</p>
      </div>
    );
  }
  
  if (currentUser) {
    return isAdmin() ? <Navigate to="/admin/dashboard" /> : <Navigate to="/games" />;
  } else {
    return <Navigate to="/" />;
  }
};

// ==================== ENHANCED RUMMY GAME WRAPPER ====================

const EnhancedRummyGameWrapper = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { gameId } = useParams();
  
  if (!gameId) {
    return (
      <div className="game-error-container">
        <h3>Error: No Game ID</h3>
        <p>Unable to load game. Please go back to the lobby.</p>
        <button onClick={() => navigate('/games')}>Back to Lobby</button>
      </div>
    );
  }
  
  const handleGameEnd = () => {
    console.log('Game ended, navigating to main games page');
    navigate('/games');
  };
  
  return (
    <ErrorBoundary fallback={
      <div className="game-error-container">
        <h3>Error Loading Game</h3>
        <p>The game could not be loaded. Please try again.</p>
        <button onClick={() => navigate('/games')}>Back to Lobby</button>
      </div>
    }>
      <EnhancedRummyGame
        user={currentUser}
        gameId={gameId}
        onGameEnd={handleGameEnd}
      />
    </ErrorBoundary>
  );
};

// ==================== CRITICAL: MULTIPLAYER GAME WRAPPER ====================

const MultiplayerGameWrapper = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { gameId } = useParams();
  
  if (!gameId) {
    return (
      <div className="game-error-container">
        <h3>Error: No Game ID</h3>
        <p>Unable to load game. Please go back to the lobby.</p>
        <button onClick={() => navigate('/multiplayer')}>Back to Lobby</button>
      </div>
    );
  }
  
  const handleGameEnd = () => {
    console.log('Game ended, navigating to multiplayer lobby');
    navigate('/multiplayer');
  };
  
  return (
    <ErrorBoundary fallback={
      <div className="game-error-container">
        <h3>Error Loading Game</h3>
        <p>The game could not be loaded. Please try again.</p>
        <button onClick={() => navigate('/multiplayer')}>Back to Lobby</button>
      </div>
    }>
      <EnhancedRummyGame
        user={currentUser}
        isMultiplayer={true}
        gameId={gameId}
        onGameEnd={handleGameEnd}
      />
    </ErrorBoundary>
  );
};

// ==================== CASH RUMMY WAITING ROOM WRAPPER (REDIRECT) ====================

const CashRummyWaitingRoomWrapper = () => {
  const { gameId } = useParams();
  const location = useLocation();
  return <Navigate to={`/game/${gameId}`} state={location.state} replace />;
};

// ==================== WAITING ROOM WRAPPER (REDIRECT) ====================

const FreePlayWaitingRoomWrapper = () => {
  const { gameId } = useParams();
  const location = useLocation();
  return <Navigate to={`/game/${gameId}`} state={location.state} replace />;
};

// ==================== FREE PLAY WRAPPER ====================

const FreePlaySelectionWrapper = () => {
  return (
    <ProtectedRoute>
      <div className="free-play-container">
        <Header />
        <ErrorBoundary fallback={<div>Error loading Free Play selection. Please try again.</div>}>
          <FreePlayGameSelection />
        </ErrorBoundary>
      </div>
    </ProtectedRoute>
  );
};

// ==================== GAME SELECTION WRAPPER ====================

const GameSelectionWrapper = () => {
  return (
    <ProtectedRoute>
      <div>
        <Header />
        <GameSelection />
      </div>
    </ProtectedRoute>
  );
};

// ==================== MULTIPLAYER LOBBY WRAPPER ====================

const MultiplayerLobbyWrapper = () => {
  return (
    <ProtectedRoute>
      <div>
        <Header />
        <MultiplayerGameLobby />
      </div>
    </ProtectedRoute>
  );
};

// ==================== ENHANCED PRACTICE COMPONENTS ====================

const EnhancedPracticeSession = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const { difficulty = 'beginner', aiLevel = 1 } = location.state || {};

  return (
    <div className="enhanced-practice-session">
      <EnhancedRummyGame 
        user={currentUser}
        difficulty={difficulty}
        aiLevel={aiLevel}
      />
    </div>
  );
};

const EnhancedPracticeStats = () => {
  const { currentUser } = useAuth();
  
  return (
    <div className="enhanced-practice-stats">
      <PracticeStats user={currentUser} />
    </div>
  );
};

const EnhancedPracticeAchievements = () => {
  const { currentUser } = useAuth();
  
  return (
    <div className="enhanced-practice-achievements">
      <PracticeAchievements user={currentUser} />
    </div>
  );
};

// ==================== GAME LAYOUT WRAPPER COMPONENTS ====================

const PracticeGameWrapper = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { difficulty, aiLevel } = location.state || {};
  
  const practicePlayers = [
    { id: 1, name: 'YOU', type: 'human', position: 'bottom', cards: 13, score: 0, isTurn: true },
    { id: 2, name: 'AI 1', type: 'ai', position: 'top', cards: 13, score: 0, isTurn: false },
    { id: 3, name: 'AI 2', type: 'ai', position: 'left', cards: 13, score: 0, isTurn: false },
    { id: 4, name: 'AI 3', type: 'ai', position: 'right', cards: 13, score: 0, isTurn: false }
  ];

  const sampleCards = ['AH', '2H', '3H', '4H', '5H', '6H', '7H', '8H', '9H', '10H', 'JH', 'QH', 'KH'];

  const handleStartGame = () => {
    console.log('Starting practice game with difficulty:', difficulty);
  };

  const handleExit = () => {
    navigate('/games');
  };

  return (
    <GameLayout
      gameType="practice"
      gameTitle={`Practice - ${difficulty?.toUpperCase() || 'BEGINNER'}`}
      players={practicePlayers}
      userCards={sampleCards}
      gameStats={{
        score: 0,
        deckCount: 52,
        discardCount: 0,
        difficulty: difficulty
      }}
      onExit={handleExit}
      onStart={handleStartGame}
      showStartButton={false}
    />
  );
};

const TournamentGameWrapper = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const tournamentPlayers = [
    { id: 1, name: 'YOU', type: 'human', position: 'bottom', cards: 13, chips: 15000, isTurn: true },
    { id: 2, name: 'Player2', type: 'human', position: 'top', cards: 13, chips: 12000, isTurn: false },
    { id: 3, name: 'Player3', type: 'ai', position: 'left', cards: 13, chips: 18000, isTurn: false },
    { id: 4, name: 'Player4', type: 'ai', position: 'right', cards: 13, chips: 9000, isTurn: false }
  ];

  const sampleCards = ['AS', '2S', '3S', '4S', '5S', '6S', '7S', '8S', '9S', '10S', 'JS', 'QS', 'KS'];

  const handleStartGame = () => {
    console.log('Starting tournament game');
  };

  const handleExit = () => {
    navigate('/games');
  };

  return (
    <GameLayout
      gameType="tournament"
      gameTitle="Weekly Championship"
      players={tournamentPlayers}
      userCards={sampleCards}
      gameStats={{
        tournamentInfo: { round: 3, prizePool: 5000 },
        userChips: 15000,
        deckCount: 48,
        discardCount: 4
      }}
      onExit={handleExit}
      onStart={handleStartGame}
      showStartButton={false}
    />
  );
};

const CashGameWrapper = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tableId, buyIn } = location.state || {};
  
  const cashPlayers = [
    { id: 1, name: 'YOU', type: 'human', position: 'bottom', cards: 13, stack: 985, isTurn: true },
    { id: 2, name: 'ProPlayer', type: 'human', position: 'top', cards: 13, stack: 1250, isTurn: false },
    { id: 3, name: 'AI_Bot1', type: 'ai', position: 'left', cards: 13, stack: 1100, isTurn: false },
    { id: 4, name: 'AI_Bot2', type: 'ai', position: 'right', cards: 13, stack: 900, isTurn: false }
  ];

  const sampleCards = ['AD', '2D', '3D', '4D', '5D', '6D', '7D', '8D', '9D', '10D', 'JD', 'QD', 'KD'];

  const handleStartGame = () => {
    console.log('Starting cash game at table:', tableId);
  };

  const handleExit = () => {
    navigate('/games');
  };

  return (
    <GameLayout
      gameType="cash"
      gameTitle={`$${buyIn || 10} Cash Table`}
      players={cashPlayers}
      userCards={sampleCards}
      gameStats={{
        buyIn: buyIn || 10,
        pot: 45,
        userStack: 985,
        userBalance: 1500,
        deckCount: 42,
        discardCount: 6
      }}
      onExit={handleExit}
      onStart={handleStartGame}
      showStartButton={false}
    />
  );
};

// ==================== MAIN APP COMPONENT ====================

function App() {
  return (
    <ConfigProvider>
      <AuthProvider>
        <WalletProvider>
          <WebSocketProvider>
            <GameProvider>
              <PracticeProvider>
                <Router>
                  <div className="app enhanced-app">
                    <TestbedBanner />
                    <Routes>
                      {/* ==================== PUBLIC ROUTES ==================== */}
                      <Route path="/" element={<Home />} />
                      <Route path="/how-to-play" element={<HowToPlay />} />
                      <Route 
                        path="/super-admin" 
                        element={
                          <SuperAdminRoute>
                            <SuperAdminDashboard />
                          </SuperAdminRoute>
                        } 
                      />
                    
                      <Route 
                        path="/login" 
                        element={
                          <PublicRoute>
                            <Login />
                          </PublicRoute>
                        } 
                      />
                      <Route 
                        path="/change-password" 
                        element={<ForceChangePassword />} 
                      />
                      <Route 
                        path="/admin-dashboard" 
                        element={
                          <AdminRoute>
                            <AdminDashboard />
                          </AdminRoute>
                        } 
                      />
                      <Route 
                        path="/register" 
                        element={
                          isTestbed() && !isRegistrationOpen() ? (
                            <RegistrationClosed />
                          ) : (
                            <PublicRoute>
                              <Register />
                            </PublicRoute>
                          )
                        } 
                      />
                      <Route 
                        path="/reset-password" 
                        element={
                          <PublicRoute>
                            <ResetPassword />
                          </PublicRoute>
                        } 
                      />

                      {/* ==================== ADMIN ROUTES ==================== */}
                      <Route 
                        path="/admin/login" 
                        element={
                          <AdminPublicRoute>
                            <AdminLogin />
                          </AdminPublicRoute>
                        } 
                      />
                      
                      <Route 
                        path="/admin/dashboard" 
                        element={
                          <AdminRoute>
                            <AdminDashboard />
                          </AdminRoute>
                        } 
                      />

                      {/* ==================== GAME SELECTION ROUTES ==================== */}
                      
                      {/* Main Game Selection Page */}
                      <Route 
                        path="/games" 
                        element={<GameSelectionWrapper />} 
                      />

                       {/* ==================== MULTIPLAYER ROUTES ==================== */}
                      <Route 
                        path="/multiplayer" 
                        element={<MultiplayerLobbyWrapper />} 
                      />
                      
                                         
                      {/* MAIN GAME ROUTE - This is where the game table loads */}
                      <Route path="/game/:gameId" element={<EnhancedRummyGame />} />
                      
                      {/* Free Play Game Selection */}
                      <Route 
                        path="/multiplayer/free-play" 
                        element={<FreePlaySelectionWrapper />} 
                      />
                      
                      {/* Waiting Room */}
                      <Route 
                        path="/multiplayer/free-play/waiting/:gameId" 
                        element={<FreePlayWaitingRoomWrapper />} 
                      />
                      
                      {/* MULTIPLAYER GAME ROUTE - Alternative path for multiplayer games */}
                      <Route 
                        path="/multiplayer/game/:gameId" 
                        element={
                          <ProtectedRoute>
                            <div className="multiplayer-game-page">
                              <MultiplayerGameWrapper />
                            </div>
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Cash Rummy & Tournament Routes */}
                      <Route path="/cash-rummy" element={<CashRummy />} />
                      <Route 
                        path="/cash-rummy/waiting/:gameId" 
                        element={<CashRummyWaitingRoomWrapper />} 
                      />
                      <Route path="/tournament" element={<div><Header /><Tournament /></div>} />
                      <Route path="/tournaments" element={<div><Header /><Tournament /></div>} />
                      <Route path="/tournament/:id/results" element={<div><Header /><TournamentResults /></div>} />
                      <Route path="/tournament/:id/monitor" element={<TournamentMonitor />} />

                      {/* Custom Room Lobby */}
                      <Route
                        path="/custom-room/:code"
                        element={
                          <ProtectedRoute>
                            <div>
                              <Header />
                              <CustomRoomLobby />
                            </div>
                          </ProtectedRoute>
                        }
                      />

                      {/* ==================== ENHANCED PRACTICE ROUTES ==================== */}
                      <Route 
                        path="/practice-games" 
                        element={
                          <ProtectedRoute>
                            <div>
                              <Header />
                              <EnhancedPracticeLobby />
                            </div>
                          </ProtectedRoute>
                        } 
                      />

                      <Route
                        path="/enhanced-practice/session"
                        element={
                          <ProtectedRoute>
                            <PracticeSessionPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* ==================== USER PROFILE ROUTES ==================== */}
                      <Route 
                        path="/dashboard" 
                        element={
                          <ProtectedRoute>
                            <div>
                              <Header />
                              <Dashboard />
                            </div>
                          </ProtectedRoute>
                        } 
                      />
                      
                      <Route 
                        path="/profile" 
                        element={
                          <ProtectedRoute>
                            <div>
                              <Header />
                              <Profile />
                            </div>
                          </ProtectedRoute>
                        } 
                      />
                      
                      <Route 
                        path="/add-funds" 
                        element={
                          <ProtectedRoute>
                            <div>
                              <Header />
                              <AddFunds />
                            </div>
                          </ProtectedRoute>
                        } 
                      />
                      
                      <Route 
                        path="/transaction-history" 
                        element={
                          <ProtectedRoute>
                            <div>
                              <Header />
                              <TransactionHistory />
                            </div>
                          </ProtectedRoute>
                        } 
                      />
                      
                      <Route 
                        path="/settings" 
                        element={
                          <ProtectedRoute>
                            <div>
                              <Header />
                              <Settings />
                            </div>
                          </ProtectedRoute>
                        } 
                      />
                      
                      <Route 
                        path="/leaderboard" 
                        element={
                          <ProtectedRoute>
                            <div>
                              <Header />
                              <Leaderboard />
                            </div>
                          </ProtectedRoute>
                        } 
                      />

                      {/* ==================== CATCH ALL ROUTE ==================== */}
                      <Route path="*" element={<AppRedirect />} />
                    </Routes>

                    {/* Enhanced Footer */}
                    <footer className="enhanced-footer">
                      <div className="footer-section">
                        <p>&copy; 2035 JV Rummy Game. All rights reserved.</p>
                        <div className="footer-links">
                          <a href="/games">Games</a>
                          <a href="/multiplayer">Multiplayer</a>
                          <a href="/practice-games">Practice</a>
                          <a href="/cash-games">Cash Games</a>
                          <a href="/tournaments">Tournaments</a>
                        </div>
                      </div>
                    </footer>
                  </div>
                </Router>
              </PracticeProvider>
            </GameProvider>
          </WebSocketProvider>
        </WalletProvider>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;